import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    queueRecommendationReload(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {{ line: number, quantity: number, action: string }} config
   */
  updateQuantity(config) {
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        queueRecommendationReload(this);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line
   * @param {{ errors: string }} parsedResponseText
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      queueRecommendationReload(this);
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
      queueRecommendationReload(this);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      queueRecommendationReload(this);
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} updatedCart
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {{ updateButtonStates?: () => void }} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

/**
 * Debounced queue so recommendation reload waits until drawer morph/render settles.
 * Uses a per-root timer to avoid duplicate reloads from multiple cart events.
 * @param {ParentNode} root
 * @param {number} delay
 */
function queueRecommendationReload(root = document, delay = 350) {
  const key = '__cartRecommendationReloadTimer';

  const anyRoot = /** @type {any} */ (root);
  if (anyRoot[key]) {
    clearTimeout(anyRoot[key]);
  }

  anyRoot[key] = window.setTimeout(() => {
    loadCartRecommendations(root).finally(() => {
      if (anyRoot[key]) {
        clearTimeout(anyRoot[key]);
        anyRoot[key] = null;
      }
    });
  }, delay);
}

function getRecommendationButtonText(type = 'add') {
  const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';

  if (type === 'adding') {
    return isArabic ? 'جارٍ الإضافة...' : 'Adding...';
  }

  if (type === 'added') {
    return isArabic ? 'تمت الإضافة' : 'Added';
  }

  if (type === 'choose') {
    return isArabic ? 'اختر الخيارات' : 'Choose options';
  }

  return isArabic ? 'أضف إلى السلة' : 'Add to cart';
}

/**
 * @param {ParentNode} root
 */
function attachRecommendationButtons(root = document) {
  root.querySelectorAll('.cart-rec-item__btn[data-variant-id]').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    if (button.dataset.bound === 'true') return;

    button.dataset.bound = 'true';

    button.addEventListener('click', async () => {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalText = button.textContent || getRecommendationButtonText('add');

      button.setAttribute('disabled', 'disabled');
      button.textContent = getRecommendationButtonText('adding');

      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
            }),
          })
        );

        const data = await response.json();

        button.textContent = getRecommendationButtonText('added');

        document.dispatchEvent(new CartAddEvent(data));

        document.querySelectorAll('cart-items-component').forEach((component) => {
          queueRecommendationReload(component, 500);
        });
      } catch (error) {
        button.removeAttribute('disabled');
        button.textContent = originalText;
      }
    });
  });
}

/**
 * @param {ParentNode} root
 */
async function loadCartRecommendations(root = document) {
  const container = root.querySelector('#cart-recommendations');
  const productsContainer = root.querySelector('#cart-recommendations__products');

  if (!container || !productsContainer) return;

  const productId = container.dataset.productId;
  if (!productId) {
    container.style.display = 'none';
    productsContainer.innerHTML = '';
    return;
  }

  try {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(
      `${routeRoot}recommendations/products.json?product_id=${productId}&limit=4&intent=related`,
      { credentials: 'same-origin' }
    );

    if (!response.ok) {
      throw new Error(`Recommendations request failed: ${response.status}`);
    }

    const data = await response.json();
    const products = Array.isArray(data.products) ? data.products : [];

    if (!products.length) {
      container.style.display = 'none';
      productsContainer.innerHTML = '';
      return;
    }

    container.style.display = '';

    const html = products
      .map((product) => {
        const firstVariant = product.variants && product.variants.length ? product.variants[0] : null;
        const hasSingleVariant = product.variants && product.variants.length === 1;
        const isAvailable = !!product.available;
        const image = product.featured_image || '';
        const price = firstVariant ? (firstVariant.price / 100).toFixed(3) : '';
        const buttonLabel =
          hasSingleVariant && firstVariant && isAvailable
            ? getRecommendationButtonText('add')
            : getRecommendationButtonText('choose');

        return `
          <div class="cart-rec-item">
            <a href="${product.url}" class="cart-rec-item__image">
              ${image ? `<img src="${image}" alt="${escapeHtml(product.title)}" loading="lazy" width="70" height="70">` : ''}
            </a>
            <div class="cart-rec-item__info">
              <a href="${product.url}" class="cart-rec-item__title">${escapeHtml(product.title)}</a>
              ${price ? `<span class="cart-rec-item__price">${price} KWD</span>` : ''}
              ${
                hasSingleVariant && firstVariant && isAvailable
                  ? `<button type="button" class="cart-rec-item__btn" data-variant-id="${firstVariant.id}">${buttonLabel}</button>`
                  : `<a href="${product.url}" class="cart-rec-item__btn">${buttonLabel}</a>`
              }
            </div>
          </div>
        `;
      })
      .join('');

    productsContainer.innerHTML = html;
    attachRecommendationButtons(productsContainer);
  } catch (error) {
    console.error(error);
    container.style.display = 'none';
    productsContainer.innerHTML = '';
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}